import { useCallback, useEffect, useRef, useState } from 'react';
import './GoForGold.css';
import {
  INITIAL_STATE, MIN, TARGET,
  fmt, goldQty, reward, dayNo, canJoin, offersLive,
  streakCopy,
} from './campaign';
import { fetchIndependence, claimIndependence } from './api';
import { shareWithFriends, lumpsumScreen, offersBrands, goHome } from '../Helper/native';
import IntroSplash from './components/IntroSplash';
import TopBar from './components/TopBar';
import Hero from './components/Hero';
import StreakSection from './components/StreakSection';
import InvestSection from './components/InvestSection';
import FaqSection from './components/FaqSection';
import Footer from './components/Footer';
import Ticker from './components/Ticker';
import { Scrim, EarningsSheet, ConfirmSheet, SuccessSheet, ClaimSheet } from './components/Sheets';
import WinSheet from './components/WinSheet';
import { ClaimPopup } from './components/Overlays';

/* Reveal via inline style rather than a class: React re-renders rewrite
   className on elements with dynamic classes (e.g. the amount field's
   gfg-focus/gfg-err), which would strip an imperatively-added class and
   fade the element back out. Inline styles survive re-renders. */
const revealEl = (el) => { el.style.opacity = '1'; el.style.transform = 'none'; };

export default function GoForGold() {
  /* ---------------- campaign state (static defaults until the API lands) ---------------- */
  const [S, setS] = useState(INITIAL_STATE);
  const [api, setApi] = useState(null); // resp.independence — drives the sections when present

  /* ---------------- ui state ---------------- */
  const [sheet, setSheet] = useState(null); // earnings | confirm | success | claim | win
  const [winInfo, setWinInfo] = useState(null);
  const [successInfo, setSuccessInfo] = useState({ title: '', body: '', streak: '', back: '', next: '' });
  const [amount, setAmount] = useState(5000);
  const [display, setDisplay] = useState('5,000');
  const [paying, setPaying] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [flipIndex, setFlipIndex] = useState(-1);
  const [bigwin, setBigwin] = useState(null);
  const [loading, setLoading] = useState(true);         // full-screen shimmer until initial API load
  const [refreshing, setRefreshing] = useState(false);   // shimmer while re-fetching after claim
  const [introVisible, setIntroVisible] = useState(false);

  const pageRef = useRef(null);
  const fieldRef = useRef(null);
  const inputRef = useRef(null);
  const wasOk = useRef(true);

  const streakStatus = api && api.streak ? api.streak.status : null;
  const showStreak = !api || streakStatus === 'ACTIVE' || streakStatus === 'COMPLETED';
  const streakOnTop = showStreak && (!api || streakStatus === 'ACTIVE');

  /* ---------------- sheets ---------------- */
  const openSheet = useCallback((name) => setSheet(name), []);
  const closeAll = useCallback(() => setSheet(null), []);

  const goInvest = useCallback(() => {
    setSheet(null);
    fieldRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => inputRef.current && inputRef.current.focus({ preventScroll: true }), 700);
  }, []);

  /* ---------------- reveal-on-scroll ---------------- */
  useEffect(() => {
    const root = pageRef.current;
    const rvs = [...root.querySelectorAll('.gfg-rv')];
    if (!('IntersectionObserver' in window)) { rvs.forEach(revealEl); return undefined; }
    const io = new IntersectionObserver((es) => {
      es.forEach((e) => { if (e.isIntersecting) { revealEl(e.target); io.unobserve(e.target); } });
    }, { threshold: .12, rootMargin: '0px 0px -6% 0px' });
    rvs.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [showStreak, streakOnTop]);

  /* ---------------- intro ---------------- */
  useEffect(() => {
    document.body.classList.toggle('gfg-locked', introVisible);
    return () => document.body.classList.remove('gfg-locked');
  }, [introVisible]);

  /* fetch campaign data while the intro splash is on screen; the page keeps
     its static defaults until (and unless) resp.independence arrives */
  useEffect(() => {
    let alive = true;
    fetchIndependence()
      .then((m) => {
        if (!alive || !m) return;
        setApi(m.ind);
        setS((prev) => ({ ...prev, ...m.S }));
      })
      .catch((err) => console.error('campaign fetch failed:', err))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const endIntro = useCallback(() => {
    setIntroVisible(false);
    const rvs = [...pageRef.current.querySelectorAll('.gfg-rv')];
    rvs.forEach((el, i) => {
      if (el.getBoundingClientRect().top < window.innerHeight) {
        setTimeout(() => revealEl(el), 80 + i * 55);
      }
    });
  }, []);

  /* ---------------- earnings + claim ---------------- */
  const openEarnings = useCallback(() => openSheet('earnings'), [openSheet]);
  const openClaim = useCallback(() => openSheet('claim'), [openSheet]);

  /* the API minimum wins over the static ₹200 */
  const minAmount = (api && api.gold && api.gold.minAmount) || MIN;

  const celebrate = useCallback((n, label, unit, decimal) => setBigwin({ n, label, unit, decimal, id: Date.now() }), []);

  const refreshAfterClaim = useCallback(() => {
    setRefreshing(true);
    fetchIndependence()
      .then((m) => {
        if (!m) return;
        setApi(m.ind);
        setS((prev) => ({ ...prev, ...m.S }));
      })
      .catch(() => {})
      .finally(() => setRefreshing(false));
  }, []);

  const dismissPopup = useCallback(() => {
    setBigwin(null);
    refreshAfterClaim();
  }, [refreshAfterClaim]);

  const doClaim = useCallback(() => {
    if (claiming) return;
    setClaiming(true);
    claimIndependence()
      .then((res) => {
        if (!res || res.error) return; // failed — sheet stays open for a retry
        setS((prev) => ({ ...prev, claim: 'claimed' }));
        setSheet(null);
        const rc = res.rewardsClaimed || {};
        const isSats = rc.type === 'bonus_sats';
        const unit = isSats ? 'Sats' : (rc.unit || 'mg');
        celebrate(rc.value || 0, isSats ? 'Sats credited' : '', unit, !isSats);
      })
      .catch((err) => console.error('claim failed:', err))
      .finally(() => setClaiming(false));
  }, [claiming, celebrate]);

  /* dispatcher for the CTA actions the API sends */
  const runAction = useCallback((action) => {
    switch (action) {
      case 'SIP': lumpsumScreen(250, false); break; // native Gold SIP investment screen
      case 'BUY_GOLD': lumpsumScreen(null, true); break;
      case 'EARNINGS_SHEET': openEarnings(); break;
      case 'CLAIM': openClaim(); break;
      case 'GET_CARD': break; // card ordering not wired yet
      case 'VOUCHERS': offersBrands(); break; // native Brands screen
      default: break; // unknown actions are no-ops
    }
  }, [amount, minAmount, openEarnings, openClaim]);

  const eAction = useCallback((action) => {
    if (action === 'CLAIM') return openClaim();
    if (action === 'BUY_GOLD') { setSheet(null); return lumpsumScreen(250, true); } // native lumpsum gold purchase
    if (action) { setSheet(null); return goHome(); } // "Keep the streak going" → native home
    if (S.claim === 'claimable') return openClaim();
    if (S.claim === 'claimed' || S.claim === 'credited') return setSheet(null);
    setSheet(null);
    goHome();
  }, [S, openClaim]);

  /* ---------------- streak CTA ---------------- */
  const copy = streakCopy(S);
  const onStreakCta = useCallback((act) => {
    if (act === 'invest') goInvest();
    else if (act === 'sip') lumpsumScreen(250, false); // native Gold SIP investment screen
    else if (act === 'earnings') openEarnings();
    else if (act === 'claim') openClaim();
    else if (act === 'wallet' || act === 'remind') return; // no in-page feedback
    else runAction(act); // API actions (SIP, CLAIM, ...)
  }, [goInvest, openEarnings, openClaim, runAction]);

  /* ---------------- amount ---------------- */
  const onAmountInput = useCallback((value) => {
    const raw = value.replace(/[^0-9]/g, '').slice(0, 7);
    const n = raw ? parseInt(raw, 10) : 0;
    setDisplay(raw ? fmt(n) : '');
    setAmount(n);
    const ok = n >= minAmount;
    wasOk.current = ok;
  }, [minAmount]);

  const onChip = useCallback((v) => {
    setAmount(v);
    setDisplay(fmt(v));
    wasOk.current = true;
    if (navigator.vibrate) navigator.vibrate(8);
  }, []);

  /* ---------------- purchase ---------------- */
  const openConfirm = useCallback(() => {
    if (!offersLive(S)) return;
    if (api && api.gold && api.gold.cta && api.gold.cta.enabled === false) return;
    if (amount < minAmount) return; // field already shows the minimum-amount error
    lumpsumScreen(amount, true); // native lumpsum gold purchase screen
  }, [S, api, amount, minAmount]);

  /* the streak-aware success moment, used after any streak-advancing buy */
  const showStreakWin = useCallback((st) => {
    const complete = st.claim === 'claimable';
    const dayIndex = Math.max(0, st.done - 1);
    setWinInfo({
      title: complete ? 'Streak complete' : (st.done === 1 ? "You're in" : 'Day ' + st.done + ' done'),
      sub: complete ? 'All 7 SIPs done. Your 2% is ready.'
        : (st.done === 1 ? 'Day 1 of your gold streak is locked in.'
          : (st.window - st.done > 0 ? (TARGET - st.done) + ' more to unlock your 2%' : 'One more to go')),
      dayIndex,
      progress: st.done + ' of ' + TARGET + ' SIPs done' + (st.window === 8 ? ' · day ' + dayNo(st) + ' of 8' : ''),
      ctaLabel: complete ? 'Claim ₹' + fmt(reward(st)) : 'Keep going',
      act: complete ? 'claim' : 'remind',
      window: st.window,
    });
    setSheet('win');
  }, []);

  const winAction = useCallback(() => {
    if (winInfo && winInfo.act === 'claim') openClaim();
    else setSheet(null);
  }, [winInfo, openClaim]);

  const pay = useCallback(() => {
    setPaying(true);
    setTimeout(() => {
      setPaying(false);
      const wasJoined = S.joined;
      const next = { ...S };
      if (!next.broken) {
        if (canJoin(next) || next.joined) {
          next.joined = true;
          if (next.done < next.window) next.done++;
          next.todayDone = true;
        }
        next.invested += amount;
        if (next.done >= TARGET && next.missed < 2) next.claim = 'claimable';
      }
      setS(next);
      setSuccessInfo({
        title: next.claim === 'claimable' ? 'Streak complete' : (wasJoined ? 'Gold bought' : "You're in"),
        body: goldQty(amount) + ' of 24K gold is now in your vault.',
        streak: next.broken ? 'Streak already ended'
          : next.claim === 'claimable' ? 'Streak complete · ₹' + fmt(reward(next)) + ' unlocked'
          : next.done + ' of ' + TARGET + ' SIPs done',
        back: '₹' + fmt(Math.round(amount * 0.05)),
        next: next.claim === 'claimable' ? 'Claim your 2% reward'
          : next.broken ? '—' : 'Tomorrow before 11:59 pm',
      });
      if (!next.broken && next.joined) showStreakWin(next);
      else openSheet('success');
      if (!next.broken) {
        setFlipIndex(next.done - 1);
        setTimeout(() => setFlipIndex(-1), 750);
      }
      if (navigator.vibrate) navigator.vibrate([10, 40, 20]);
    }, 1050);
  }, [S, amount, showStreakWin, openSheet]);

  /* ---------------- misc ---------------- */
  const share = useCallback(() => {
    shareWithFriends('Hey! 🪙 GoSats Go For Gold is Live! ✨ 🪙 2% back on 7-day Gold SIP streaks 🚚 5% back + delivery at ₹1/- on Gold purchases (min ₹200) https://dl.gosats.io/home');
  }, []);

  return (
    <div className="gfg-page" ref={pageRef}>
      <IntroSplash visible={introVisible} playKey={0} onEnd={endIntro} />

      <TopBar
        onBack={goHome}
        onShare={share}
      />

      <div className="gfg-phone">
        <div className="gfg-stage">
          <div className="gfg-bgtex" />
          <Hero />

          {showStreak && streakOnTop && (
            <StreakSection
              S={S}
              copy={copy}
              card={api && api.streak ? api.streak.card : null}
              streakStatus={streakStatus}
              flipIndex={flipIndex}
              onCta={onStreakCta}
              onOpenEarnings={openEarnings}
              onInvest={goInvest}
            />
          )}

          <InvestSection
            S={S}
            gold={api && api.gold}
            min={minAmount}
            amount={amount}
            display={display}
            onAmountInput={onAmountInput}
            onChip={onChip}
            onBuyNow={openConfirm}
            onDelivery={() => lumpsumScreen(250, true)}
            fieldRef={fieldRef}
            inputRef={inputRef}
            first={!streakOnTop}
          />

          {showStreak && !streakOnTop && (
            <StreakSection
              S={S}
              copy={copy}
              card={api && api.streak ? api.streak.card : null}
              streakStatus={streakStatus}
              flipIndex={flipIndex}
              onCta={onStreakCta}
              onOpenEarnings={openEarnings}
              onInvest={goInvest}
            />
          )}

          <FaqSection />

          {/* <Footer /> */}
        </div>
      </div>

      <Ticker S={S} />

      <Scrim on={sheet !== null} onClose={closeAll} />
      <EarningsSheet on={sheet === 'earnings'} S={S} earnings={api && api.streak ? api.streak.earnings : null} onCta={eAction} />
      <ConfirmSheet on={sheet === 'confirm'} S={S} amount={amount} paying={paying} onPay={pay} onCancel={closeAll} />
      <SuccessSheet on={sheet === 'success'} info={successInfo} onDone={closeAll} />
      <ClaimSheet on={sheet === 'claim'} S={S} claimInfo={api && api.claim} rewardPref={api && api.rewardPreference} claiming={claiming} onClaim={doClaim} />
      <WinSheet on={sheet === 'win'} info={winInfo} onCta={winAction} />

      {(loading || refreshing) && <div className="gfg-shimmer-overlay" />}
      <ClaimPopup trigger={bigwin} onClose={dismissPopup} />
    </div>
  );
}
